import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { Button, ButtonTypeEnum } from "@b2bportal/core-ui";
import type { Lodging } from "@b2bportal/lodging-api";
import { getPercentage, useI18nContext } from "@hopper-b2b/i18n";
import {
  HotelPriceText,
  HotelShopPriceLoading,
  HotelShopSearch,
} from "@tenant/hotels";
import { Pill, PillTone } from "@tenant/ui";
import clsx from "clsx";
import { useHotelShopBookingSection } from "./hooks";
import defaultStyles from "./HotelShopBookingSection.module.scss";
import { Experiments, useExperiment } from "@/modules/experiments/src";

export interface HotelShopBookingSectionProps extends PlatformComponentProps {
  hotel: Lodging;
  goToNextStep: () => void;
}

const COMPONENT_KEY = CoreHotelsComponent.HotelShopBookingSection;

export const HotelShopBookingSection = (
  props: HotelShopBookingSectionProps
) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { t, locale } = useI18nContext();
  const { isExperimentVariant } = useExperiment();
  const hotelFundedOffersEnabled = isExperimentVariant(
    Experiments.HOTEL_FUNDED_OFFERS.name,
    Experiments.HOTEL_FUNDED_OFFERS.variants.available
  );

  const viewProps = useHotelShopBookingSection(props);

  const {
    isLoading,
    isAvailable,
    showPointsPricing,
    totalPoints,
    totalPrice,
    totalPriceNotDiscounted,
    totalNights,
    rooms,
    percentOff,
    text,
    nextStep,
    isShopSearchVisible,
  } = viewProps;

  return (
    <div className={cn("container")}>
      <div className={clsx(block, props.className)}>
        {isLoading ? (
          <HotelShopPriceLoading />
        ) : (
          <>
            <div className={cn("pricing-info")}>
              {/* Discount badge temporarily hidden
              {hotelFundedOffersEnabled &&
              isAvailable &&
              percentOff != null &&
              percentOff > 0 ? (
                <Pill
                  className={cn("discount-badge")}
                  size="custom"
                  tone={PillTone.custom}
                >
                  {t("hotelCard.discountBadge", {
                    percent: getPercentage(percentOff, locale),
                  })}
                </Pill>
              ) : null}
              */}
              {isAvailable ? (
                <HotelPriceText
                  showPointsPricing={showPointsPricing}
                  totalPoints={totalPoints}
                  totalPrice={totalPrice}
                  totalPriceNotDiscounted={totalPriceNotDiscounted}
                  totalNights={totalNights}
                  rooms={rooms}
                  pricePerNightText={text.pricePerNight.final}
                />
              ) : (
                <p className={cn("unavailable")}>{viewProps.unavailable}</p>
              )}
            </div>
            {isShopSearchVisible && (
              <HotelShopSearch className={cn("search")} />
            )}
            <Button
              disabled={nextStep == null}
              className={cn("choose-room")}
              onClick={() => nextStep?.()}
              buttonType={ButtonTypeEnum.Primary}
            >
              {text.roomCTA}
            </Button>
            {text.helperText && (
              <p className={cn("helper-text")}>{text.helperText}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};
